// THE "NEXT ACTION" COLUMN (v4.0.0 Projects list, design p05;
// docs/ui-refresh/01-home-workspace.md §2.4).
//
// Nothing is stored: the line is DERIVED from the project's stage and its open
// tasks, every time the list draws. Pure — data in, a line out, no DOM and no
// network — so the priority order is tested in node
// (test/projects-next-action.test.mjs).
//
// Priority, first match wins:
//   1. past its deadline        → chase (orange)
//   2. Ready to Issue           → "Sign and issue"
//   3. Review                   → "{lead} to finish review"
//   4. a task sent for checking → "Check steel beam (Karan)"
//   5. a task waiting on someone→ "Waiting on R3 Design"
//   6. the next task with a date→ "Schedule of condition today", "Site visit Mon 26/10"
//   7. nothing                  → null (the list shows "—")
// Issued and Complete projects have no next action.

const DAY = 86400000;
const pad2 = (n) => String(n).padStart(2, '0');
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const ymdDate = (v) => {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
const cap1 = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const lc1 = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
const words = (s, n) => { const w = String(s || '').trim().split(/\s+/); return w.length > n ? w.slice(0, n).join(' ') : w.join(' '); };
/** "Check steel beam over kitchen, rear extension" → "Check steel beam over kitchen". */
const head = (title) => String(title || '').split(/,|\s[—–]\s|\s-\s/)[0].trim();
/** "Party wall award — schedule of condition" → "Schedule of condition"; no dash → the title. */
const actionPart = (title) => {
  const parts = String(title || '').split(/\s[—–]\s/);
  return cap1((parts.length > 1 ? parts.slice(1).join(' — ') : parts[0]).trim());
};

/** "today", "tomorrow", "Mon 26/10"; late when the date has passed. */
export function duePhrase(dueDate, today) {
  const d = ymdDate(dueDate);
  if (!d) return { text: '', late: false };
  const n = Math.round((startOfDay(d) - startOfDay(today)) / DAY);
  if (n < 0) return { text: 'overdue', late: true };
  if (n === 0) return { text: 'today', late: false };
  if (n === 1) return { text: 'tomorrow', late: false };
  return { text: `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`, late: false };
}

/**
 * p: the project. tasks: its OPEN tasks (any order). opts:
 *   today      Date
 *   overdue    whether the project itself is past its deadline (the list's rule)
 *   firstName  (userId) → "Karan" ('' when unknown)
 * Returns { text, late } or null.
 */
export function nextActionFor(p, tasks = [], { today = new Date(), overdue = false, firstName = () => '' } = {}) {
  const status = String((p && p.status) || '');
  if (status === 'Issued' || status === 'Complete' || status === 'Void') return null;
  const leadId = String((p && p.lead) || '').split('/').pop();
  const lead = leadId ? firstName(leadId) : '';

  if (overdue) {
    if (status === 'Review') return { text: lead ? `Chase ${lead}’s review` : 'Chase the review', late: true };
    if (status === 'Ready to Issue') return { text: 'Sign and issue', late: true };
    return { text: lead ? `Chase ${lead}` : 'Agree a new date', late: true };
  }
  if (status === 'Ready to Issue') return { text: 'Sign and issue', late: false };
  if (status === 'Review') return { text: lead ? `${lead} to finish review` : 'Finish the review', late: false };

  const open = (tasks || []).filter((t) => t && t.state !== 'done');
  const check = open.find((t) => t.state === 'in_review' && t.checkerId);
  if (check) {
    const h = words(head(check.title), 3);
    const who = firstName(check.checkerId);
    const verb = /^check\b/i.test(h) ? h : `Check ${lc1(h)}`;
    return { text: who ? `${verb} (${who})` : verb, late: false };
  }
  const blocked = open.find((t) => t.state === 'blocked' && t.blocker && t.blocker.waitingOnName);
  if (blocked) return { text: `Waiting on ${String(blocked.blocker.waitingOnName).split(/\s[—–]\s/)[0].trim()}`, late: false };

  const dated = open.filter((t) => t.state !== 'blocked' && ymdDate(t.dueDate))
    .sort((a, b) => ymdDate(a.dueDate) - ymdDate(b.dueDate));
  if (dated.length) {
    const t = dated[0];
    const when = duePhrase(t.dueDate, today);
    const what = /^site visit\b/i.test(String(t.title || '')) ? 'Site visit' : actionPart(t.title);
    return { text: `${what} ${when.text}`.trim(), late: when.late };
  }
  return null;
}
